import {
  DOMINANT_NEW_COVERAGE_SHARE,
  MAX_INLINE_LIST_ITEMS,
  MIN_DOMINANT_NEW_COVERAGE_COUNT,
  MIN_ROWS_FOR_COVERAGE_CONCENTRATION,
  TOP_AGENT_CONCENTRATION_SHARE,
  TOP_TWO_AGENTS_CONCENTRATION_SHARE,
} from "./runsSummaryConstants"
import {
  formatCountEntries,
  getName,
  getNewEntries,
  getTopEntries,
  incrementCount,
  isUnsuccessfulRow,
} from "./runsSummaryDetailData"
import { formatCount, pluralize } from "./runsSummaryFormat"
import type {
  CountEntry,
  RunsPageSummaryLookups,
  RunsPageSummaryRow,
} from "./runsSummaryTypes"

export function buildFailureClusterItem(
  rows: RunsPageSummaryRow[],
  lookups: RunsPageSummaryLookups,
  rowNoun: string,
): string | null {
  const unsuccessfulRows = rows.filter(isUnsuccessfulRow)
  if (unsuccessfulRows.length === 0) {
    return null
  }

  const clusterCounts = new Map<string, CountEntry>()
  unsuccessfulRows.forEach((row) => {
    const testSet = getName(
      row.test_set_id,
      lookups.testSetNames,
      "Unknown test set",
    )
    const agent = getName(row.agent_id, lookups.agentNames, "Unknown agent")
    const label =
      testSet && agent
        ? `${testSet} / ${agent}`
        : (testSet ?? agent ?? "Unknown")
    incrementCount(clusterCounts, label, label)
  })
  const [topCluster] = getTopEntries(clusterCounts, 1)
  const clusterClause = topCluster
    ? `; largest cluster is ${topCluster.label} (${formatCount(topCluster.count)})`
    : ""

  return `${formatCount(unsuccessfulRows.length)} loaded ${pluralize(
    unsuccessfulRows.length,
    rowNoun,
  )} failed or finished unsuccessfully${clusterClause}.`
}

export function buildNewCoverageItem(
  label: string,
  currentCounts: Map<string, CountEntry>,
  previousCounts: Map<string, CountEntry>,
): string | null {
  const newEntries = getNewEntries(
    currentCounts,
    previousCounts,
    MAX_INLINE_LIST_ITEMS,
  )
  if (newEntries.length === 0) {
    return null
  }
  return `${label} first seen in this window vs the prior window: ${formatCountEntries(
    newEntries,
  )}.`
}

export function buildNewCoverageDigItem(
  label: string,
  currentCounts: Map<string, CountEntry>,
  previousCounts: Map<string, CountEntry>,
  guidance: string,
): string | null {
  const newEntries = getNewEntries(
    currentCounts,
    previousCounts,
    MAX_INLINE_LIST_ITEMS,
  )
  if (newEntries.length === 0) {
    return null
  }

  return `${label} newly observed in this window: ${formatCountEntries(
    newEntries,
  )}; ${guidance}.`
}

export function buildCoverageConcentrationDigItem(
  topAgents: CountEntry[],
  totalRows: number,
  rowNoun: string,
): string | null {
  if (
    topAgents.length === 0 ||
    totalRows < MIN_ROWS_FOR_COVERAGE_CONCENTRATION
  ) {
    return null
  }

  const topAgentShare = topAgents[0]?.count ? topAgents[0].count / totalRows : 0
  const topTwoAgents = topAgents.slice(0, 2)
  const topTwoShare =
    topTwoAgents.reduce((sum, entry) => sum + entry.count, 0) / totalRows
  if (
    topAgentShare < TOP_AGENT_CONCENTRATION_SHARE &&
    topTwoShare < TOP_TWO_AGENTS_CONCENTRATION_SHARE
  ) {
    return null
  }

  return `Coverage is concentrated on ${formatCountEntries(
    topTwoAgents,
  )}; review these high-volume agents separately before treating aggregate ${rowNoun} trends as representative.`
}

function getDominantNewEntry(
  currentCounts: Map<string, CountEntry>,
  previousCounts: Map<string, CountEntry>,
  totalRows: number,
): CountEntry | null {
  const [topNewEntry] = getNewEntries(
    currentCounts,
    previousCounts,
    currentCounts.size,
  )
  if (!topNewEntry) {
    return null
  }

  const minimumDominantCount = Math.max(
    MIN_DOMINANT_NEW_COVERAGE_COUNT,
    Math.ceil(totalRows * DOMINANT_NEW_COVERAGE_SHARE),
  )
  return topNewEntry.count >= minimumDominantCount ? topNewEntry : null
}

export function buildCoverageDriverItem(params: {
  agentCounts: Map<string, CountEntry>
  personaCounts: Map<string, CountEntry>
  previousAgentCounts: Map<string, CountEntry>
  previousPersonaCounts: Map<string, CountEntry>
  previousTestSetCounts: Map<string, CountEntry>
  rowCount: number
  rowNoun: string
  testSetCounts: Map<string, CountEntry>
}): string | null {
  const dominantEntries = [
    {
      dimension: "test set",
      entry: getDominantNewEntry(
        params.testSetCounts,
        params.previousTestSetCounts,
        params.rowCount,
      ),
    },
    {
      dimension: "persona",
      entry: getDominantNewEntry(
        params.personaCounts,
        params.previousPersonaCounts,
        params.rowCount,
      ),
    },
    {
      dimension: "agent",
      entry: getDominantNewEntry(
        params.agentCounts,
        params.previousAgentCounts,
        params.rowCount,
      ),
    },
  ].filter(
    (candidate): candidate is { dimension: string; entry: CountEntry } =>
      candidate.entry !== null,
  )
  const [dominantEntry] = dominantEntries
  if (!dominantEntry) {
    return null
  }

  const looksScriptedOrSmoke =
    /smoke|script|single[- ]?turn|one[- ]?turn/i.test(dominantEntry.entry.label)
  const prefix = looksScriptedOrSmoke
    ? `New smoke/scripted ${dominantEntry.dimension} coverage dominates this window`
    : `New ${dominantEntry.dimension} coverage dominates this window`

  return `${prefix}: ${dominantEntry.entry.label} (${formatCount(
    dominantEntry.entry.count,
  )} of ${formatCount(params.rowCount)} loaded ${pluralize(
    params.rowCount,
    params.rowNoun,
  )}). Metric movement may reflect test mix; compare like-for-like before treating it as agent behavior.`
}
